/**
 * SAVINGS NORMALIZATION UTILITY
 * a Single source of truth for FTE × automation × cost savings calculations
 * Eliminates legacy paths and ensures consistency across all components
 */
import {ValueSummary} from "@shared/interfaces/valueAssessment.interface.ts";
import {
    Activity,
    ActivitySavings,
    NormalizedSavingsResult,
    PillarSavings, RegionalSavings
} from "@shared/interfaces/results/normalizedSavingsResult.ts";
/**
 * Core normalization function - calculates annual savings from FTE × automation × cost only
 */
export function calculateAnnualSavingsFromActivities(activities: Activity[]): NormalizedSavingsResult {

  if (!activities || activities.length === 0) {
    return {
      annualSavingsTotal: 0,
      fiveYearValue: 0,
      totalFTEs: 0,
      activityBreakdown: [],
      pillarBreakdown: [],
      regionalBreakdown: []
    }
  }

  // Calculate activity-level savings
  const activityBreakdown: ActivitySavings[] = activities.map(activity => {
    const { ftes, automationRate, costPerFTE } = activity
    const annualSavings = ftes * (automationRate / 100) * costPerFTE
    
    return {
      task: activity.task,
      pillar: activity.pillar,
      ftes,
      automationRate,
      costPerFTE,
      annualSavings,
      region: activity.region,
      frequency: activity.frequency
    }
  })

  // Calculate total annual savings
  const annualSavingsTotal = activityBreakdown.reduce((sum, activity) => sum + activity.annualSavings, 0)
  const fiveYearValue = annualSavingsTotal * 5
  const totalFTEs = activities.reduce((sum, activity) => sum + activity.ftes, 0)

  // Group by pillar for pillar breakdown
  const pillarGroups = activities.reduce((groups: Record<string, Activity[]>, activity) => {
    const pillar = activity.pillar
    if (!groups[pillar]) groups[pillar] = []
    groups[pillar].push(activity)
    return groups
  }, {})

  const pillarBreakdown: PillarSavings[] = Object.entries(pillarGroups).map(([pillar, pillarActivities]) => {
    const totalFTEs = pillarActivities.reduce((sum, activity) => sum + activity.ftes, 0)
    const totalAnnualSavings = pillarActivities.reduce((sum, activity) => {
      return sum + (activity.ftes * (activity.automationRate / 100) * activity.costPerFTE)
    }, 0)
    const weightedAutomationRate = totalFTEs > 0 ? 
      pillarActivities.reduce((sum, activity) => sum + (activity.automationRate * activity.ftes), 0) / totalFTEs : 0
    
    return {
      pillar,
      totalFTEs,
      weightedAutomationRate,
      totalAnnualSavings,
      activityCount: pillarActivities.length
    }
  })

  // Group by region for regional breakdown
  const regionalGroups = activities.reduce((groups: Record<string, Activity[]>, activity) => {
    const region = activity.region
    if (!groups[region]) groups[region] = []
    groups[region].push(activity)
    return groups
  }, {})

  const regionalBreakdown: RegionalSavings[] = Object.entries(regionalGroups).map(([region, regionalActivities]) => {
    const totalFTEs = regionalActivities.reduce((sum, activity) => sum + activity.ftes, 0)
    const totalAnnualSavings = regionalActivities.reduce((sum, activity) => {
      return sum + (activity.ftes * (activity.automationRate / 100) * activity.costPerFTE)
    }, 0)
    const totalCost = regionalActivities.reduce((sum, activity) => sum + (activity.ftes * activity.costPerFTE), 0)
    const averageCostPerFTE = totalFTEs > 0 ? totalCost / totalFTEs : 0
    
    return {
      region,
      totalFTEs,
      totalAnnualSavings,
      averageCostPerFTE
    }
  })

  return {
    annualSavingsTotal,
    fiveYearValue,
    totalFTEs,
    activityBreakdown,
    pillarBreakdown,
    regionalBreakdown
  }
}

/**
 * Calculate total deliverable automation savings from activities with regional cost weighting
 */
export function calculateDeliverableAutomationSavings(activities: any[]): number {

  if (!activities || activities.length === 0) {
    return 0;
  }

  // Regional FTE costs
  const regionalCosts: Record<string, number> = {
    'NA': 95000,
    'North America': 95000,
    'EMEA': 85000,
    'APAC': 65000,
    'Asia Pacific': 65000,
    'India': 35000,
    'Shared Services': 55000,
    'Other': 75000 // Default fallback
  };

  let totalSavings = 0;

  activities.forEach(activity => {
    const fteCount = activity.fteCount || activity.ftes || activity.totalFTEs || 0;
    const automationPercent = activity.automationPercent || activity.deliverableAutomation || activity.weightedAutomationPotential || 0;
    
    // Handle regional breakdown
    if (activity.regionBreakdown) {
      Object.entries(activity.regionBreakdown).forEach(([region, ftes]: [string, any]) => {
        const regionalCost = regionalCosts[region] || regionalCosts['Other'];
        const regionalSavings = (ftes as number) * (automationPercent / 100) * regionalCost;
        totalSavings += regionalSavings;
        
      });
    } else {
      // Fallback to overall calculation if no regional breakdown
      const defaultCost = regionalCosts['Other'];
      const savings = fteCount * (automationPercent / 100) * defaultCost;
      totalSavings += savings;
      
    }
  });

  return totalSavings;
}

/**
 * Normalize region names to match keys in fteCosts maps
 */
function normalizeRegionKey(region: string): string {
  const rn = String(region || '').toLowerCase().replace(/[_\-]/g, ' ').trim();
  if (rn.includes('north america') || rn === 'americas' || rn === 'na') return 'North America';
  if (rn === 'emea') return 'EMEA';
  if (rn === 'apac' || rn.includes('asia pacific')) return 'APAC';
  if (rn === 'india') return 'India';
  if (rn.includes('shared')) return 'Shared Services';
  return region;
}

/**
 * Calculate total benchmark automation savings from activities with regional cost weighting
 * @param activities - normalized activity array
 * @param benchmarkData - database-provided benchmark rows (must include task_label and/or pillar)
 * @param options - optional map of regionalCosts; if omitted, tries to infer from activities; else uses defaults
 */
export function calculateBenchmarkAutomationSavings(
  activities: any[],
  benchmarkData?: any[],
  options?: { regionalCosts?: Record<string, number> }
): number {

  if (!activities || activities.length === 0) {
    return 0;
  }

  // ✅ CRITICAL: Ensure benchmark data is loaded from database
  if (!benchmarkData || benchmarkData.length === 0) {
    console.error('🚨 CRITICAL ERROR: No benchmark data provided from database!');
    console.error('Cannot calculate benchmark automation without database benchmark rates.');
    return 0; // Fail fast - no fallback to hardcoded rates allowed
  }

  // Resolve regional costs in priority order:
  // 1) options.regionalCosts
  // 2) activities[*].fteCostsByRegion / regionalCosts / meta
  // 3) sane database-aligned defaults
  let regionalCosts: Record<string, number> | undefined = options?.regionalCosts;
  if (!regionalCosts) {
    for (const a of activities) {
      const candidate =
        a?.fteCosts ||
        a?.fteCostsByRegion ||
        a?.regionalCosts ||
        a?.meta?.fteCostsByRegion ||
        a?.meta?.regionalCosts;
      if (candidate && typeof candidate === 'object') {
        regionalCosts = candidate as Record<string, number>;
        break;
      }
    }
  }
  if (!regionalCosts) {
    // Defaults aligned with database example you shared (NA 75k, EMEA 60k, APAC 45k, India 25k, Shared 40k)
    regionalCosts = {
      'North America': 75000,
      'EMEA': 60000,
      'APAC': 45000,
      'India': 25000,
      'Shared Services': 40000,
      'Other': 75000
    };
  }

  // Build quick helpers
  const getBenchmarkPercentForActivity = (activityName: string): number => {
    const name = (activityName || '').toLowerCase().trim();
    // exact match on task_label first
    const exact = benchmarkData.find((b: any) => (b.task_label || '').toLowerCase().trim() === name);
    if (exact && Number.isFinite(exact.benchmark_percent)) return Number(exact.benchmark_percent);
    return 0;
  };

  const avgBenchmarkForPillar = (pillar: string): number => {
    const key = (pillar || '').toLowerCase().trim();
    const matches = (benchmarkData || []).filter((b: any) => (b.pillar || b.pillar_name || '').toLowerCase().trim() === key);
    if (matches.length === 0) return 0;
    const sum = matches.reduce((s: number, b: any) => s + (Number(b.benchmark_percent) || 0), 0);
    return sum / matches.length;
  };

  // Optional map for common name normalizations (when upstream names differ slightly)
  const activityToBenchmarkMapping: Record<string, string> = {
    'Preparation of reclassifications': 'Preparation of reclassifications',
    'Daily Reconciliation and adjustments': 'Reconciliation and adjustments - Daily',
    'Monthly Reconciliation and adjustments': 'Reconciliation and adjustments - Monthly',
    'Balance sheet reconciliation - adjustments': 'Balance sheet reconciliation - adjustments',
    'Balance sheet reconciliation - preparation': 'Balance sheet reconciliation - preparation',
    'Review & approval of accruals and provisions': 'Review & approval of accruals and provisions',
    'Manual journal entries, adjustments, transfers, error corrections': 'Manual journal entries, adjustments, transfers, error corrections',
    'Sub-ledger reconciliations, banks recs, production quantities & stock': 'Sub-ledger reconciliations, bank reconciliation, production quantities and stock',
    'Period open/closing, cost allocation, forex revaluation, depreciation, project and order settlement': 'Period open/closing, cost allocation, forex revaluation, depreciation, project and order settlement'
  };

  let totalSavings = 0;

  for (const activity of activities) {
    const fteCount = activity.fteCount || activity.ftes || activity.totalFTEs || 0;
    const activityName =
      activity.activityName || activity.activity || activity.taskName || activity.task || activity.name || '';
    const pillar = activity.pillar || '';

    // Try direct, then mapped, then pillar-average
    let benchmarkPercent = getBenchmarkPercentForActivity(activityName);
    if (!benchmarkPercent && activityToBenchmarkMapping[activityName]) {
      benchmarkPercent = getBenchmarkPercentForActivity(activityToBenchmarkMapping[activityName]);
    }
    if (!benchmarkPercent && pillar) {
      benchmarkPercent = avgBenchmarkForPillar(pillar);
    }

    if (!benchmarkPercent || benchmarkPercent <= 0) {
      continue; // nothing to add for this activity
    }

    if (activity.regionBreakdown) {
      for (const [region, ftes] of Object.entries(activity.regionBreakdown)) {
        const key = normalizeRegionKey(region);
        const regionalCost =
          regionalCosts[key] ??
          regionalCosts['Other'] ??
          // choose a stable fallback if 'Other' is missing
          regionalCosts['North America'] ??
          0;
        const count = Number(ftes) || 0;
        if (!regionalCost || !count) continue;
        totalSavings += count * (benchmarkPercent / 100) * regionalCost;
      }
    } else {
      // No per-region breakdown; use a default representative cost
      const defaultCost =
        regionalCosts['Other'] ??
        regionalCosts['North America'] ??
        regionalCosts['EMEA'] ??
        regionalCosts['APAC'] ??
        0;
      if (!defaultCost || !fteCount) continue;
      totalSavings += fteCount * (benchmarkPercent / 100) * defaultCost;
    }
  }

  return totalSavings;
}

/**
 * Extract activities from valueSummary data structure
 */
export function extractActivitiesFromValueSummary(valueSummary: ValueSummary): Activity[] {

  // Check multiple potential data paths
  const taskFTEAllocation = valueSummary?.taskFTEAllocation
  const automationPercentages = taskFTEAllocation?.automationPercentages || {}
  const fteMatrix = taskFTEAllocation?.fteMatrix || {}

  // Prefer authentic regional cost data captured during assessments; fall back to defaults when absent
  const fteCostCandidates = [
    taskFTEAllocation?.fteCostsByRegion,
    taskFTEAllocation?.regionalCosts,
    (taskFTEAllocation as any)?.meta?.fteCostsByRegion,
    (valueSummary as any)?.fteCostsByRegion,
  ]

  const resolvedFteCosts: Record<string, number> = {}
  for (const candidate of fteCostCandidates) {
    if (!candidate || typeof candidate !== 'object') continue
    Object.entries(candidate).forEach(([regionKey, cost]) => {
      const numericCost = typeof cost === 'number' ? cost : Number(cost)
      if (!Number.isFinite(numericCost) || numericCost <= 0) return
      const normalizedKey = normalizeRegionKey(regionKey)
      if (resolvedFteCosts[normalizedKey]) return
      resolvedFteCosts[normalizedKey] = numericCost
    })
  }

  const activities: Activity[] = []
  
  // Extract from FTE matrix with automation percentages
  Object.entries(fteMatrix).forEach(([taskName, taskData]: [string, any]) => {
    if (taskData && typeof taskData === 'object') {
      Object.entries(taskData).forEach(([region, ftes]: [string, any]) => {
        if (typeof ftes === 'number' && ftes > 0) {
          const automationRate = automationPercentages[taskName] || 0
          const normalizedRegion = normalizeRegionKey(region)
          const costPerFTE =
            resolvedFteCosts[normalizedRegion] ??
            resolvedFteCosts[region] ??
            getCostPerFTEByRegion(region)
          const pillar = determinePillarByTask(taskName)
          
          activities.push({
            task: taskName,
            pillar,
            ftes,
            automationRate,
            costPerFTE,
            region,
            frequency: 'daily' // Default assumption, you might want to track this differently
          })
        }
      })
    }
  })

  
  return activities
}

/**
 * Get cost per FTE by region (default values - should be made configurable)
 */
export function getCostPerFTEByRegion(region: string): number {
  const regionCosts: Record<string, number> = {
    'APAC': 65000,
    'EMEA': 85000,
    'India': 35000,
    'North America': 95000,
    'Shared Services': 55000
  }
  
  return regionCosts[region] || 65000 // Default fallback
}

/**
 * Calculate regional automation savings for an activity
 */
export function calculateRegionalAutomationSavings(
  activity: any,
  automationPercent: number
): number {
  let totalSavings = 0;

  const regionalFTEs = activity.regionBreakdown || activity.regionalFTEs || activity.fteBreakdown || activity.regions || {};
  const dbFteCosts = activity.fteCosts || {};

  Object.entries(regionalFTEs).forEach(([region, fteCount]) => {
    const fteCountValue = Number(fteCount) || 0;
    const rn = String(region).toLowerCase().replace(/\s+_/g,' ').trim();
    // Use database-provided FTE costs (from taskFTEAllocation.fteCostsByRegion) when available
    const regionalCost =
      dbFteCosts[region] ??
      (dbFteCosts['North America'] && (rn === 'north america' || rn === 'americas') ? dbFteCosts['North America'] :
      dbFteCosts.EMEA && rn === 'emea' ? dbFteCosts.EMEA :
      dbFteCosts.APAC && rn === 'apac' ? dbFteCosts.APAC :
      dbFteCosts.India && rn === 'india' ? dbFteCosts.India :
      dbFteCosts['Shared Services'] && rn.includes('shared') ? dbFteCosts['Shared Services'] :
      getCostPerFTEByRegion(region));
    const regionalSavings = fteCountValue * regionalCost * (automationPercent / 100);
    totalSavings += regionalSavings;
  });

  return totalSavings;
}



/**
 * Determine pillar by task name (enhanced logic for better classification)
 */
function determinePillarByTask(taskName: string): string {
  const taskLower = taskName.toLowerCase()
  
  // Account Reconciliation patterns
  if (taskLower.includes('balance sheet') || taskLower.includes('reconciliation')) {
    return 'Account Reconciliation'
  }
  
  // Journal Entry Automation patterns
  if (taskLower.includes('manual journal') || taskLower.includes('journal entries')) {
    return 'Journal Entry Automation'
  }
  
  // Intercompany patterns
  if (taskLower.includes('intercompany') || taskLower.includes('cross charges') || 
      taskLower.includes('transaction postings')) {
    return 'Intercompany'
  }
  
  // Close Management patterns
  if (taskLower.includes('period open') || taskLower.includes('closing') || 
      taskLower.includes('cost allocation')) {
    return 'Close Management'
  }
  
  return 'Other Processes'
}

export function  getAnnualSavings(valueSummary: ValueSummary): NormalizedSavingsResult|null {
    const activities: Activity[] = extractActivitiesFromValueSummary(valueSummary);

    if (activities.length === 0) {
        return null
    }

    return calculateAnnualSavingsFromActivities(activities);


}
